import {CheckoutConfig, ProductViewModel, SiteConfig} from "./models";

// What every visitor's pages need, as one file on the API's CDN, which the API rewrites after every change to it
// (tristar-corp-api's CatalogPublisher): the published products, the site config and the shop's, just as GET /product,
// /site-config and /checkout/config answer. Read instead of those, once per page load, so browsing doesn't wait for the
// API to start (a cold start takes about a second).
export interface SiteCatalog {
    products: ProductViewModel[];
    siteConfig: SiteConfig;
    checkout: CheckoutConfig;
}

export const CATALOG_URL: string = `${CDN_BASE}/catalog/site.json`;

let loading: Promise<SiteCatalog> | undefined;
// Once this tab has changed any of it (an administrator), the API's own answers: the CDN's copy takes a moment to follow.
let changedHere = false;

// One try per page load: when the file can't be had, the API's answers instead.
function load(): Promise<SiteCatalog> {
    loading ??= fetch(CATALOG_URL, { cache: 'no-cache' }).then(async response => {
        if (!response.ok) {
            throw new Error(`${CATALOG_URL}: ${response.status}`);
        }
        const catalog = await response.json();
        if (!Array.isArray(catalog?.products) || !catalog.siteConfig || !catalog.checkout) {
            throw new Error(`${CATALOG_URL}: not a catalog`);
        }
        return catalog as SiteCatalog;
    });
    return loading;
}

// The part of the catalog pick chooses, or the API's answer (fromApi) when the catalog can't be read or this tab has
// changed it.
export async function fromCatalog<T>(pick: (catalog: SiteCatalog) => T, fromApi: () => Promise<T>): Promise<T> {
    if (changedHere) {
        return await fromApi();
    }
    let catalog: SiteCatalog;
    try {
        catalog = await load();
    } catch {
        return await fromApi();
    }
    return pick(catalog);
}

// Every API request that succeeded (http.ts): one that may have changed the catalog sends this tab to the API for it.
export function noteRequest(method: string, path: string): void {
    if (method === 'GET' || changedHere) {
        return;
    }
    const route = path.split('?')[0];
    if (route === '/product' || route.startsWith('/product/') || route === '/site-config' || route === '/seed-data/product') {
        changedHere = true;
    }
}

// For tests: as a new page load.
export function resetCatalog(): void {
    loading = undefined;
    changedHere = false;
}
